import { DocumentSnapshot } from 'firebase/firestore';
import FBInterface, { OperationResult, AuthResult, AuthResponse } from './fbinterface';
import {User} from 'firebase/auth';
import {TaskData, TaskDataMeta, TaskDataTableRow, TaskStatus} from '../types/tasks.types'
import { ContentSetData, ContentStatus } from '../types/content.types';
import { getEmptyContentData } from '../pages/content-one/content-one';
import { getEmptyTasksData } from '../pages/tasks-view/tasks-view';

const USER_TASKS_COL:string='usertasks/';
const USER_CONTENT_COL:string='contentsets/';
const CONTENT_AUTOINC_DOC:string='contentAutoId/counter';

const CONTENT_STORAGE_BASE:string='https://firebasestorage.googleapis.com/v0/b/gamestolearnenglish-fb.appspot.com/o/contentsets%2F';
const JSON_TAIL='.json?alt=media';
const JSON_DASH:string='%2F';

export interface MyUserType{
	username:string;
	usermail:string;
}

interface TaskResults{
	[key:string]:{score:number;	isComplete:boolean;	}
}

export interface TasksDoc {
	tasks?: string[];
}

export interface ContentDoc {
	[key: string]: string[];  // Category keys map to content set arrays
}

const DELETE_META:boolean=true;
const NO_DELETE_META:boolean=false;

export default class FBWrapper{

	private usermail:string='';
	private fBInterface=new FBInterface();

	//////////////////////////////////////////////////////////////////////////////////
	//login
	
	addAuthListener(callback:(user: MyUserType) => void){
		this.fBInterface.setInterfacePersistence();
		this.fBInterface.addAuthListener((user:User|null)=>{
			if(user!==null){
				if(user.displayName!==null&&user.email!==null){
					if(user.email)this.usermail=user.email;
					callback({username:user.displayName, usermail:user.email});
				}else{
					callback({username:'', usermail:''});
				}
			}else{
				callback({username:'', usermail:''});
			}
		});
	}

	async signIn(): Promise<AuthResponse> {
		const response = await this.fBInterface.signIn();
		if(response.result !== AuthResult.Success) {
			console.log('Sign in Error =', response.errorMessage);
		}
		return response;
	}

	async signOut(): Promise<AuthResponse> {
		const response = await this.fBInterface.signOut();
		if(response.result !== AuthResult.Success) {
			console.log('Sign out Error =', response.errorMessage);
		}
		return response;
	}

	//////////////////////////////////////////////////////////////////////////////////
	//tasks
	async getTaskList(): Promise<{status: TaskStatus, tasksList: string[]}> {
		try {
			const tasksData = await this.fBInterface.getOneDoc<TasksDoc>(USER_TASKS_COL+this.usermail);
			let tasksList: string[] = [];
			if(tasksData.tasks !== undefined) tasksList = tasksData.tasks;
			return {status: TaskStatus.Default, tasksList};
		} catch(err) {
			return {status: TaskStatus.GetListError, tasksList: []};
		}
	}

	async getTaskData(taskName:string): Promise<{status: TaskStatus, taskData: TaskData}> {
		try {
			let taskResultsList:TaskResults={};
			let myMeta:TaskDataMeta|null=null;
			let scoreType='';
			const res = await this.fBInterface.getAllDocs(USER_TASKS_COL+this.usermail+'/'+taskName);
			res.forEach((doc:DocumentSnapshot)=> {
				if(doc.id==='meta'){
					myMeta=this.processTaskMeta(doc);
					scoreType=myMeta.scoreType;
				}else{
					taskResultsList=this.addTaskResult(doc, taskResultsList);
				}
			});
			if(myMeta===null){
				return {status: TaskStatus.GetTaskMetaError, taskData: getEmptyTasksData()};
			}else{
				let taskTableData=this.prepareTaskTableData(taskResultsList, scoreType);
				return {status: TaskStatus.Default, taskData: {tableData:taskTableData,metaData:myMeta}};
			}
		} catch(err) {
			return {status: TaskStatus.GetTaskDataError, taskData: getEmptyTasksData()};
		}
	}

	async resetTaskData(taskTitle:string): Promise<TaskStatus> {
		try {
			await this.taskDeleter(taskTitle, NO_DELETE_META);
			return TaskStatus.Default;
		} catch(err) {
			return TaskStatus.ResetTaskFailed;
		}
	}

	async deleteTask(taskTitle:string, tasksList:string[]): Promise<{status: TaskStatus, tasksList: string[]}> {
		let newTasksList=tasksList.slice();
		newTasksList.splice(tasksList.indexOf(taskTitle),1);
		try {
			await this.taskDeleter(taskTitle, DELETE_META);
			const result = await this.fBInterface.setDocument(USER_TASKS_COL+this.usermail, {tasks:newTasksList});
			if(result === OperationResult.Success){
				return {status: TaskStatus.Default, tasksList: newTasksList};
			}else{
				return {status: TaskStatus.DeleteTaskFailed, tasksList};
			}
		} catch(err) {
			return {status: TaskStatus.DeleteTaskFailed, tasksList};
		}
	}

	async createTask(taskData:TaskDataMeta, tasksList:string[]): Promise<{status: TaskStatus, tasksList: string[]}> {
		let newTasksList:string[]=tasksList.slice();
		newTasksList.push(taskData.taskTitle);
		try {
			const result1 = await this.fBInterface.setDocument(USER_TASKS_COL+this.usermail, {tasks:newTasksList});
			if(result1 === OperationResult.Success){
				const result2 = await this.fBInterface.setDocument(USER_TASKS_COL+this.usermail+'/'+taskData.taskTitle+'/meta', taskData);
				if(result2 === OperationResult.Success){
					return {status: TaskStatus.CreateSuccess, tasksList: newTasksList};
				}else{
					return {status: TaskStatus.CreateDelete, tasksList: newTasksList};
				}
			}else{
				return {status: TaskStatus.CreateFailed, tasksList};
			}
		} catch(err) {
			return {status: TaskStatus.CreateFailed, tasksList};
		}
	}

	private async taskDeleter(taskTitle:string, deleteMeta:boolean): Promise<void> {
		try {
			let deleteList:string[]=[];
			const res = await this.fBInterface.getAllDocs(USER_TASKS_COL+this.usermail+'/'+taskTitle);
			res.forEach((myDoc)=>{
				if(myDoc.id!=='meta'||deleteMeta){
					deleteList.push(USER_TASKS_COL+this.usermail+"/"+taskTitle+"/"+myDoc.id);
				}
			});
			const outcome = await this.fBInterface.deleteDocuments(deleteList);
			if(!outcome){
				throw new Error('Failed to delete documents');
			}
		} catch(err) {
			throw err;
		}
	}

	//////////////////////////////////////////////////////////////////////////////////
	//content

	async getContentList(): Promise<{status: ContentStatus, contentList: string[]}> {
		try {
			const contentData = await this.fBInterface.getOneDoc<ContentDoc>(USER_CONTENT_COL+this.usermail);
			let mySets:string[]=[];
			Object.keys(contentData).forEach((myKey)=>{
				contentData[myKey].forEach((contentSet:string)=>{
					mySets.push(contentSet);
				})
			})
			return {status: ContentStatus.Default, contentList: mySets};
		} catch(err) {
			return {status: ContentStatus.GetListError, contentList: []};
		}
	}

	async getContentSet(contentSet:string): Promise<{status: ContentStatus, contentSetData: ContentSetData}> {
		let umail=this.usermail.replace(/\//g, JSON_DASH).replace(/@/g, '%40');
		let myPath=CONTENT_STORAGE_BASE+umail+JSON_DASH+contentSet+JSON_TAIL;
		try {
			const response = await this.fBInterface.getJson(myPath);
			if(response.success && response.data){
				return {status: ContentStatus.Default, contentSetData: this.processContentData(contentSet, response.data)};
			}else{
				let myEmptySet=getEmptyContentData();
				myEmptySet.title=contentSet;
				return {status: ContentStatus.GetSetError, contentSetData: myEmptySet};
			}
		} catch(err) {
			let myEmptySet=getEmptyContentData();
			myEmptySet.title=contentSet;
			return {status: ContentStatus.GetSetError, contentSetData: myEmptySet};
		}
	}

	async saveContentSet(contentSetData:ContentSetData, userContentList:string[]): Promise<{status: ContentStatus, userContentList: string[], newID: number}> {
		try {
			const {outcome, newList} = await this.addToUserContentList(userContentList, contentSetData.title);
			if(outcome === false){
				return {status: ContentStatus.SaveSetError, userContentList, newID: -1};
			}
			let myUpload = JSON.parse(JSON.stringify(contentSetData));
			const newID = await this.getContentAutoIncrement(contentSetData.myID);
			myUpload.myID = newID;
			let umail=this.usermail.replace(/\//g, JSON_DASH).replace(/@/g, '%40');
			let myPath=CONTENT_STORAGE_BASE+umail+JSON_DASH+contentSetData.title+JSON_TAIL;
			let myJson=JSON.stringify(myUpload);
			let jsonBlob:Blob=new Blob([myJson]);
			const result = await this.fBInterface.uploadJson(myPath, jsonBlob);
			if(result === OperationResult.Success){
				return {status: ContentStatus.Default, userContentList: newList, newID};
			}else{
				return {status: ContentStatus.SaveSetError, userContentList: newList, newID};
			}
		} catch(err) {
			return {status: ContentStatus.SaveSetError, userContentList, newID: -1};
		}
	}

	async deleteContentSet(setTitle:string, userContentList:string[]): Promise<{status: ContentStatus, userContentList: string[]}> {
		try {
			const {outcome, newList} = await this.deleteFromUserContentList(userContentList, setTitle);
			if(outcome === OperationResult.Success){
				let umail=this.usermail.replace(/\//g, JSON_DASH).replace(/@/g, '%40');
				let jsonPath=CONTENT_STORAGE_BASE+umail+JSON_DASH+setTitle+JSON_TAIL;
				await this.fBInterface.deleteJson(jsonPath);
				return {status: ContentStatus.Default, userContentList: newList};
			}else{
				return {status: ContentStatus.DeleteSetError, userContentList};
			}
		} catch(err) {
			return {status: ContentStatus.DeleteSetError, userContentList};
		}
	}

	async getContentAutoIncrement(curID:number): Promise<number> {
		if(curID !== -1)return curID;

		try {
			const result = await this.fBInterface.incrementCounter(CONTENT_AUTOINC_DOC);
			if(result === OperationResult.Success){
				const docData = await this.fBInterface.getOneDoc<{id:number}>(CONTENT_AUTOINC_DOC);
				return docData.id;
			}else{
				return -1;
			}
		} catch(err) {
			return -1;
		}
	}

	private async addToUserContentList(userContentList:string[], newTitle:string): Promise<{outcome: boolean, newList: string[]}> {

		if(userContentList.indexOf(newTitle) !== -1)return {outcome: true, newList: userContentList};

		let newList=userContentList.slice();
		newList.push(newTitle);
		try {
			const result = await this.fBInterface.setDocument(USER_CONTENT_COL+this.usermail, {setsList:newList});
			if(result === OperationResult.Success){
				return {outcome: true, newList};
			}else{
				return {outcome: false, newList};
			}
		} catch(err) {
			return {outcome: false, newList};
		}
	}

	private async deleteFromUserContentList(userContentList:string[], deleteTitle:string): Promise<{outcome: OperationResult, newList: string[]}> {

		if(userContentList.indexOf(deleteTitle) === -1)return {outcome: OperationResult.Success, newList: userContentList};

		let newList=userContentList.slice();
		newList.splice(newList.indexOf(deleteTitle),1);
		try {
			const result = await this.fBInterface.setDocument(USER_CONTENT_COL+this.usermail, {setsList:newList});
			return {outcome: result, newList: result === OperationResult.Success ? newList : []};
		} catch(err) {
			return {outcome: OperationResult.Failure, newList: []};
		}
	}

	processContentData(contentSet:string, res:string){
		let parsedRes=JSON.parse(res);
		let myContentData=getEmptyContentData();
		myContentData.title=contentSet;
		if(parsedRes.imsList!==undefined)myContentData.imsList=parsedRes.imsList;
		if(parsedRes.txtsList!==undefined)myContentData.txtsList=parsedRes.txtsList;
		if(parsedRes.options!==undefined)myContentData.options=parsedRes.options;
		if(parsedRes.myID!==undefined)myContentData.myID=parsedRes.myID;
		return myContentData;
	}

	////////////////////////////////////////////////////////////////////
	//process data for one task

	addTaskResult(doc:DocumentSnapshot, taskResultsList:TaskResults){
		let myUser='';
		let myScore=-1;
		if(doc.data()?.user)myUser=doc.data()?.user;
		if(doc.data()?.score)myScore=doc.data()?.score;
		let myUsername=myUser.replace(/[^a-zA-Z0-9]/g, '');
		if(doc.data()?.completed===undefined){
			if(taskResultsList[myUsername]===undefined){
				taskResultsList[myUsername]={score:-1, isComplete:false};
			}
		}else{
			let higherScore=myScore;
			if(taskResultsList[myUsername]!==undefined){
				if(taskResultsList[myUsername].score>higherScore){
					higherScore=taskResultsList[myUsername].score;
				}
			}
			taskResultsList[myUsername]={score:higherScore,isComplete:true};
		}
		return taskResultsList;
	}

	prepareTaskTableData(taskResultsList:TaskResults, scoreType:string){
		let sortType='desc';
		if(scoreType==='moves')sortType='asc';
		if(scoreType==='time')sortType='asc';

		let keys = Object.keys(taskResultsList);
		if(sortType==='asc'){
			keys.sort(function(a, b) { 
				if(taskResultsList[a].isComplete===false)return 1;
				if(taskResultsList[b].isComplete===false)return -1;
				return (taskResultsList[a].score - taskResultsList[b].score);
			})
		}else{
			keys.sort(function(a, b) { 
				return (taskResultsList[b].score - taskResultsList[a].score);
			})
		}

		let taskTableData:TaskDataTableRow[]=[];
		for(let i=0;i<keys.length;i++){
			taskTableData[i]={
				rank:i+1,
				name:keys[i],
				complete:taskResultsList[keys[i]].isComplete?'Yes':'No',
				score:taskResultsList[keys[i]].score
			}
		}
		return taskTableData;
	}

	processTaskMeta(inMeta:DocumentSnapshot):TaskDataMeta{
		return {
			contentType: inMeta.data()?.contentType||'',
			contentTitle: inMeta.data()?.contentTitle||'',
			creatorMail: inMeta.data()?.creatorMail||'',
			taskTitle: inMeta.data()?.taskTitle||'',
			scoreType: inMeta.data()?.scoreType||'',
			pageDir: inMeta.data()?.pageDir||'',
			taskOptions: inMeta.data()?.taskOptions||[]
		};
	}
}
